/**
 * Hub da Mesa — Kaysou Assist (cliente).
 *
 * Abre depois do fluxo atual de sessão de mesa. Não cria pedido, checkout
 * nem sessão: todas as ações delegam para os fluxos já existentes.
 */
import { assistLabel, assistTone } from "@/utils/tableAssistState";
import {
  Bell, ChevronRight, ExternalLink, Gift, Instagram, ReceiptText, UtensilsCrossed,
} from "lucide-react";

const TONE_STYLES = {
  idle: { bg: "#111827", fg: "#ffffff" },
  new: { bg: "#dc2626", fg: "#ffffff" },
  accepted: { bg: "#f59e0b", fg: "#111827" },
  completed: { bg: "#059669", fg: "#ffffff" },
};

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="h-12 w-12 shrink-0" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20H24v8h11.3C33.7 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-4z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.2 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4c-7.7 0-14.4 4.3-17.7 10.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35.1 26.7 36 24 36c-5.2 0-9.7-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20H24v8h11.3a12 12 0 0 1-4 5.5l6.3 5.3C41.5 35.2 44 30.1 44 24c0-1.3-.1-2.7-.4-4z" />
    </svg>
  );
}

function AssistButton({ type, request, sending, onPress, testId }) {
  const tone = assistTone(request);
  const style = TONE_STYLES[tone] || TONE_STYLES.idle;
  const waiter = type === "waiter";
  const Icon = waiter ? Bell : ReceiptText;
  const idleLabel = waiter ? "LLAMAR AL CAMARERO" : "PEDIR LA CUENTA";

  return (
    <div>
      <button
        type="button"
        onClick={() => onPress(type)}
        disabled={!!sending}
        className="flex w-full min-h-16 items-center gap-4 rounded-2xl px-5 py-3 font-bold tracking-wide text-base shadow-sm transition active:scale-[0.99] disabled:opacity-100"
        style={{ backgroundColor: style.bg, color: style.fg }}
        data-testid={testId}
      >
        <Icon className="h-7 w-7 shrink-0" aria-hidden="true" />
        <span className="flex-1 text-center">{idleLabel}</span>
        <ChevronRight className="h-6 w-6 shrink-0" aria-hidden="true" />
      </button>
      {tone !== "idle" && (
        <p className="mt-1.5 text-center text-sm font-semibold text-gray-700" data-testid={`${testId}-state`}>
          {assistLabel(type, request)}
        </p>
      )}
    </div>
  );
}

function GoogleReviewCard({ reviewUrl }) {
  return (
    <a
      href={reviewUrl || undefined}
      target="_blank"
      rel="noopener noreferrer"
      aria-disabled={!reviewUrl}
      className={`block rounded-2xl border border-gray-200 bg-white p-4 text-left shadow-sm transition active:scale-[0.99] ${reviewUrl ? "" : "pointer-events-none opacity-40"}`}
      data-testid="table-hub-rate"
    >
      <div className="flex items-start gap-3">
        <GoogleMark />
        <div className="min-w-0 flex-1">
          <p className="font-black leading-tight text-gray-900">Valora tu experiencia en Google</p>
          <p className="mt-1 text-xl tracking-[0.12em] text-[#f9ab00]" aria-label="5 estrellas">★★★★★</p>
          <p className="mt-1 text-xs text-gray-500">Tu opinión nos ayuda a mejorar</p>
        </div>
      </div>
      <span className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#1a73e8] text-sm font-black tracking-wide text-white">
        ESCRIBIR UNA RESEÑA
        <ExternalLink className="h-4 w-4" aria-hidden="true" />
      </span>
    </a>
  );
}

export function TableHub({
  restaurantName, tableLabel, themeColor = "#f97316",
  requests, sending, error,
  onOrder, onCallWaiter, onRequestBill,
  reviewUrl, instagramUrl, loyaltyUrl,
}) {
  const handlePress = (type) => (type === "waiter" ? onCallWaiter() : onRequestBill());

  return (
    <div
      className="fixed inset-0 z-[1100] overflow-y-auto bg-[#faf9f6]"
      style={{ pointerEvents: "auto" }}
      data-testid="table-hub"
    >
      <div className="mx-auto w-full max-w-md px-5 pb-10 pt-8">
        <header className="mb-7 text-center">
          <h1 className="text-lg font-bold uppercase tracking-[0.18em] text-gray-900" data-testid="table-hub-restaurant">
            {restaurantName}
          </h1>
          <div className="mx-auto mt-2 h-0.5 w-10" style={{ backgroundColor: themeColor }} />
          <p className="mt-2 text-4xl font-black tracking-wide" style={{ color: themeColor }} data-testid="table-hub-table">
            {tableLabel}
          </p>
        </header>

        {error && (
          <p className="mb-4 text-center text-sm text-red-600" data-testid="table-hub-error">{error}</p>
        )}

        <div className="space-y-4">
          <button
            type="button"
            onClick={onOrder}
            className="flex h-16 w-full items-center gap-4 rounded-2xl px-5 font-bold tracking-wide text-white shadow-sm transition active:scale-[0.99]"
            style={{ backgroundColor: themeColor }}
            data-testid="table-hub-order"
          >
            <UtensilsCrossed className="h-7 w-7 shrink-0" aria-hidden="true" />
            <span className="flex-1 text-center">PEDIR EN LA MESA</span>
            <ChevronRight className="h-6 w-6 shrink-0" aria-hidden="true" />
          </button>

          <AssistButton
            type="waiter" request={requests?.waiter} sending={sending?.waiter}
            onPress={handlePress} testId="table-hub-waiter"
          />
          <AssistButton
            type="bill" request={requests?.bill} sending={sending?.bill}
            onPress={handlePress} testId="table-hub-bill"
          />
          <GoogleReviewCard reviewUrl={reviewUrl} />
        </div>

        <div className="mt-7 flex items-center justify-center gap-4 text-sm font-semibold">
          {instagramUrl && (
            <a href={instagramUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-900 transition active:scale-[0.98]"
              data-testid="table-hub-instagram">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg"
                style={{ background: "linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)" }}
                data-testid="table-hub-instagram-icon">
                <Instagram className="h-5 w-5 text-white" aria-hidden="true" />
              </span>
              Instagram
            </a>
          )}
          {instagramUrl && loyaltyUrl && <span className="h-6 w-px bg-gray-300" aria-hidden="true" />}
          {loyaltyUrl && (
            <a href={loyaltyUrl}
              className="flex items-center gap-2 text-[#dc2626] transition active:scale-[0.98]"
              data-testid="table-hub-loyalty">
              <Gift className="h-6 w-6 text-[#dc2626]" fill="#f5c451" aria-hidden="true"
                data-testid="table-hub-loyalty-icon" />
              Programa de fidelidad
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

